import { Note } from "@/models/types";
import { useState } from "react";
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { ms } from "@/ui/moderateScale";
import { Colors } from "@/constants";
import RadioButton from "@/ui/RadioButton";
import { useTranslation } from "react-i18next";
import PrimaryButton from "@/ui/PrimaryButton";
import Icon from "@/ui/Icon";

export default function AIContentQuiz({
  quiz,
}: {
  quiz: Note["listAIContent"][number]["quiz"];
}) {
  const { t } = useTranslation();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>(
    Array(quiz?.questions?.length || 0).fill("")
  );
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const currentQuestion = quiz?.questions[currentQuestionIndex];
  const totalQuestions = quiz?.questions.length || 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const handleAnswerSelect = (answer: string) => {
    const newSelectedAnswers = [...selectedAnswers];
    newSelectedAnswers[currentQuestionIndex] = answer;
    setSelectedAnswers(newSelectedAnswers);
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Calculate score
      let correctCount = 0;
      quiz.questions.forEach((question, index) => {
        if (selectedAnswers[index] === question.correctAnswer) {
          correctCount++;
        }
      });
      setScore(correctCount);
      setShowResults(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers(Array(quiz.questions.length).fill(""));
    setShowResults(false);
  };

  const getAnswerStatusIcon = (questionIndex: number, answer: string) => {
    if (!showResults) return null;

    const isSelected = selectedAnswers[questionIndex] === answer;
    const isCorrect = quiz.questions[questionIndex].correctAnswer === answer;

    if (isCorrect) {
      return (
        <Icon
          icon="tick"
          style={{ width: ms(20), height: ms(20), tintColor: Colors.success }}
        />
      );
    } else if (isSelected) {
      return (
        <Icon
          icon="close"
          style={{ width: ms(20), height: ms(20), tintColor: Colors.error }}
        />
      );
    }

    return null;
  };

  if (showResults) {
    return (
      <ScrollView style={styles.resultsContainer}>
        <View style={styles.scoreContainer}>
          <Text style={styles.scoreText}>
            {t("aiContentQuiz.quizScore")}: {score}/{totalQuestions}
          </Text>
          <Text style={styles.scorePercentage}>
            {Math.round((score / totalQuestions) * 100)}%
          </Text>
        </View>

        {quiz.questions.map((question, questionIndex) => (
          <View key={questionIndex} style={styles.resultQuestionContainer}>
            <Text style={styles.resultQuestionText}>
              {questionIndex + 1}. {question.question}
            </Text>

            {question.options.map((option, optionIndex) => {
              const isCorrect = option === question.correctAnswer;
              const isSelected = selectedAnswers[questionIndex] === option;

              return (
                <View key={optionIndex} style={styles.resultOptionContainer}>
                  <View
                    style={[
                      styles.resultOption,
                      isCorrect && styles.correctOption,
                      isSelected && !isCorrect && styles.incorrectOption,
                    ]}
                  >
                    <Text
                      style={[
                        styles.resultOptionText,
                        (isCorrect || (isSelected && !isCorrect)) &&
                          styles.resultOptionTextHighlighted,
                      ]}
                    >
                      {option}
                    </Text>
                    {getAnswerStatusIcon(questionIndex, option)}
                  </View>
                </View>
              );
            })}
          </View>
        ))}

        <View style={styles.actionContainer}>
          <PrimaryButton
            text={t("aiContentQuiz.restartQuiz")}
            onPress={handleRestart}
          />
        </View>
      </ScrollView>
    );
  }

  if (!quiz?.questions || quiz.questions.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <ActivityIndicator color={Colors.text} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.progressContainer}>
        <Text style={styles.progressText}>
          {t("aiContentQuiz.questionProgress", {
            current: currentQuestionIndex + 1,
            total: totalQuestions,
          })}
        </Text>
        <View style={styles.progressBarContainer}>
          <View
            style={[
              styles.progressBar,
              {
                width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%`,
              },
            ]}
          />
        </View>
      </View>

      <View style={styles.questionContainer}>
        <Text style={styles.questionText}>{currentQuestion.question}</Text>

        {currentQuestion.options.map((option, index) => (
          <View key={index} style={styles.optionContainer}>
            <RadioButton
              text={option}
              isSelected={selectedAnswers[currentQuestionIndex] === option}
              onPress={() => handleAnswerSelect(option)}
            />
          </View>
        ))}
      </View>

      <View style={styles.actionContainer}>
        <PrimaryButton
          text={
            isLastQuestion
              ? t("aiContentQuiz.finishQuiz")
              : t("aiContentQuiz.nextQuestion")
          }
          onPress={handleNext}
          disabled={!selectedAnswers[currentQuestionIndex]}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: ms(16),
    backgroundColor: Colors.white,
    borderRadius: ms(12),
    borderWidth: 1,
    borderColor: Colors.gray3,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: ms(16),
    backgroundColor: Colors.white,
    borderRadius: ms(12),
    borderWidth: 1,
    borderColor: Colors.gray3,
  },
  emptyText: {
    fontSize: ms(16),
    color: Colors.subtitle,
  },
  progressContainer: {
    marginBottom: ms(24),
  },
  progressText: {
    fontSize: ms(14),
    color: Colors.subtitle,
    marginBottom: ms(8),
  },
  progressBarContainer: {
    height: ms(6),
    backgroundColor: Colors.gray3,
    borderRadius: ms(3),
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    backgroundColor: Colors.accent,
    borderRadius: ms(3),
  },
  questionContainer: {
    marginBottom: ms(24),
  },
  questionText: {
    fontSize: ms(18),
    fontWeight: "600",
    color: Colors.text,
    marginBottom: ms(20),
  },
  optionContainer: {
    marginBottom: ms(12),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
    borderWidth: 1,
    borderColor: Colors.gray3,
    padding: ms(8),
  },
  actionContainer: {
    marginTop: ms(16),
  },
  resultsContainer: {
    flex: 1,
    padding: ms(16),
    backgroundColor: Colors.white,
    borderRadius: ms(12),
    borderWidth: 1,
    borderColor: Colors.gray3,
  },
  scoreContainer: {
    backgroundColor: Colors.gray1,
    padding: ms(16),
    borderRadius: ms(12),
    marginBottom: ms(24),
    alignItems: "center",
  },
  scoreText: {
    fontSize: ms(18),
    fontWeight: "600",
    color: Colors.text,
    marginBottom: ms(8),
  },
  scorePercentage: {
    fontSize: ms(24),
    fontWeight: "700",
    color: Colors.text,
  },
  resultQuestionContainer: {
    marginBottom: ms(24),
    borderBottomWidth: 1,
    borderBottomColor: Colors.gray3,
    paddingBottom: ms(16),
  },
  resultQuestionText: {
    fontSize: ms(16),
    fontWeight: "600",
    color: Colors.text,
    marginBottom: ms(12),
  },
  resultOptionContainer: {
    marginBottom: ms(8),
  },
  resultOption: {
    backgroundColor: Colors.white,
    borderRadius: ms(8),
    borderWidth: 1,
    borderColor: Colors.gray3,
    padding: ms(12),
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  correctOption: {
    backgroundColor: Colors.success + "15",
    borderColor: Colors.success,
  },
  incorrectOption: {
    backgroundColor: Colors.error + "15",
    borderColor: Colors.error,
  },
  resultOptionText: {
    fontSize: ms(14),
    color: Colors.text,
    flex: 1,
  },
  resultOptionTextHighlighted: {
    fontWeight: "600",
  },
});
